/**
 * Agent 測試 API
 *
 * 提供 SSE Streaming 的 Agent 測試功能
 */

import { API_BASE } from './client';

/**
 * Agent 事件類型
 */
export type AgentEventType =
  | 'iteration_start'
  | 'thinking'
  | 'text_delta'
  | 'tool_call'
  | 'tool_result'
  | 'iteration_end'
  | 'complete'
  | 'error'
  // SubAgent 事件
  | 'subagent_start'
  | 'subagent_event'
  | 'subagent_end';

/**
 * Token 使用統計
 */
export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

/**
 * Tool 執行結果
 */
export interface ToolResult {
  success: boolean;
  content: string;
  parsedOutput?: unknown;
  error?: string;
  /** SubAgent 事件列表 */
  subAgentEvents?: AgentEvent[];
}

/**
 * 基礎事件
 */
export interface BaseAgentEvent {
  type: AgentEventType;
  timestamp: string;
  iteration: number;
}

/**
 * Iteration Start 事件
 */
export interface IterationStartEvent extends BaseAgentEvent {
  type: 'iteration_start';
  messageCount: number;
  estimatedTokens: number;
  rawRequest?: unknown;
}

/**
 * Thinking 事件
 */
export interface ThinkingEvent extends BaseAgentEvent {
  type: 'thinking';
  content: string;
}

/**
 * Text Delta 事件
 */
export interface TextDeltaEvent extends BaseAgentEvent {
  type: 'text_delta';
  delta: string;
  accumulated: string;
}

/**
 * Tool Call 事件
 */
export interface ToolCallEvent extends BaseAgentEvent {
  type: 'tool_call';
  toolName: string;
  toolId: string;
  input: Record<string, unknown>;
}

/**
 * Tool Result 事件
 */
export interface ToolResultEvent extends BaseAgentEvent {
  type: 'tool_result';
  toolName: string;
  toolId: string;
  result: ToolResult;
}

/**
 * Iteration End 事件
 */
export interface IterationEndEvent extends BaseAgentEvent {
  type: 'iteration_end';
  hasToolCalls: boolean;
  usage: TokenUsage;
  rawResponse?: unknown;
}

/**
 * Complete 事件
 */
export interface CompleteEvent extends BaseAgentEvent {
  type: 'complete';
  content: string;
  totalUsage: TokenUsage;
}

/**
 * Error 事件
 */
export interface ErrorEvent extends BaseAgentEvent {
  type: 'error';
  error: string;
}

// ============================================================
// SubAgent Events
// ============================================================

/**
 * SubAgent Start 事件
 */
export interface SubAgentStartEvent extends BaseAgentEvent {
  type: 'subagent_start';
  /** SubAgent 唯一識別碼 */
  subAgentId: string;
  /** 父層 Tool Use ID */
  parentToolId: string;
  /** 任務類型 */
  taskType: string;
  /** 巢狀深度 (0 = 頂層 Agent) */
  depth: number;
}

/**
 * SubAgent Event Wrapper - 包裝 SubAgent 內部事件
 */
export interface SubAgentEventWrapper extends BaseAgentEvent {
  type: 'subagent_event';
  /** SubAgent 唯一識別碼 */
  subAgentId: string;
  /** 巢狀深度 */
  depth: number;
  /** 被包裝的原始事件 */
  nestedEvent: AgentEvent;
}

/**
 * SubAgent End 事件
 */
export interface SubAgentEndEvent extends BaseAgentEvent {
  type: 'subagent_end';
  /** SubAgent 唯一識別碼 */
  subAgentId: string;
  /** 巢狀深度 */
  depth: number;
  /** 執行結果 */
  result: ToolResult;
}

/**
 * 所有事件類型
 */
export type AgentEvent =
  | IterationStartEvent
  | ThinkingEvent
  | TextDeltaEvent
  | ToolCallEvent
  | ToolResultEvent
  | IterationEndEvent
  | CompleteEvent
  | ErrorEvent
  | SubAgentStartEvent
  | SubAgentEventWrapper
  | SubAgentEndEvent;

/**
 * Agent 測試請求參數
 */
export interface AgentTestParams {
  provider_id: string;
  system_prompt?: string;
  jailbreak_preset_id?: string;
  user_message: string;
  novel_id?: string;
  /** 多章節選擇 */
  chapter_ids?: string[];
  max_iterations?: number;
  temperature?: number;
  max_tokens?: number;
  /** 對話歷史（用於多輪對話） */
  conversation_history?: Array<{
    role: 'user' | 'assistant';
    content: string;
  }>;
}

/**
 * SSE 事件處理器
 */
export interface AgentEventHandlers {
  onEvent?: (event: AgentEvent) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

/**
 * 執行 Agent 測試 (SSE Streaming)
 *
 * @example
 * ```typescript
 * const controller = new AbortController();
 *
 * await streamAgentTest(
 *   { provider_id: 'kimi-k2', user_message: '分析這個章節' },
 *   {
 *     onEvent: (event) => {
 *       if (event.type === 'text_delta') {
 *         console.log(event.delta);
 *       }
 *     },
 *     onComplete: () => console.log('Done'),
 *   },
 *   controller.signal
 * );
 *
 * // 中斷
 * controller.abort();
 * ```
 */
export async function streamAgentTest(
  params: AgentTestParams,
  handlers: AgentEventHandlers,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(`${API_BASE}/agent/test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
    credentials: 'include',
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { error?: string }).error ||
      `HTTP ${response.status}: ${response.statusText}`
    );
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        handlers.onComplete?.();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();

          if (data === '[DONE]') {
            handlers.onComplete?.();
            return;
          }

          try {
            const event = JSON.parse(data) as AgentEvent;
            handlers.onEvent?.(event);
          } catch (e) {
            console.warn('[SSE] Failed to parse event:', data, e);
          }
        }
      }
    }
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      // 正常中斷
      return;
    }
    handlers.onError?.(error as Error);
    throw error;
  }
}

// ============================================================
// AIClient 方法測試 API
// ============================================================

/**
 * AIClient 方法類型
 */
export type AIClientMethod =
  | 'chat'
  | 'chatWithJailbreak'
  | 'chatWithTool'
  | 'chatWithTools'
  | 'streamChat'
  | 'chatWithRetry'
  | 'chatRaw'
  | 'streamChatRaw';

/**
 * 測試方法請求
 */
export interface TestMethodParams {
  method: AIClientMethod;
  provider_id: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  options?: {
    system?: string;
    temperature?: number;
    maxTokens?: number;
    enableCache?: boolean;
    jailbreak?: {
      presetId?: string;
      customSystemPrompt?: string;
      customFakeConversation?: Array<{ role: 'user' | 'assistant'; content: string }>;
    };
    tool?: {
      name: string;
      description: string;
      inputSchema: Record<string, unknown>;
    };
    tools?: Array<{
      name: string;
      description: string;
      inputSchema: Record<string, unknown>;
    }>;
    maxIterations?: number;
    retry?: {
      maxRetries?: number;
      retryDelay?: number;
      enableFallback?: boolean;
    };
  };
}

/**
 * 測試方法回應
 */
export interface TestMethodResult {
  success: boolean;
  method: string;
  provider_id: string;
  model: string;
  latency_ms: number;
  content: string;
  thinking?: string;
  usage: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    cacheCreationInputTokens?: number;
    cacheReadInputTokens?: number;
    thinkingTokens?: number;
  };
  toolResult?: unknown;
  toolCalls?: Array<{ name: string; input: unknown; id: string }>;
  stopReason?: string;
  raw?: unknown;
}

/**
 * Streaming 測試請求
 */
export interface StreamTestParams {
  provider_id: string;
  system?: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  temperature?: number;
  maxTokens?: number;
  enableCache?: boolean;
}

/**
 * Streaming 事件
 */
export interface StreamTestEvent {
  type: string;
  delta?: string;
  thinking?: string;
  accumulated?: string;
  accumulatedThinking?: string;
  usage?: TokenUsage;
  error?: string;
}

/**
 * 執行 AIClient 方法測試
 */
export async function testMethod(params: TestMethodParams): Promise<TestMethodResult> {
  const response = await fetch(`${API_BASE}/agent/test-method`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { error?: string }).error ||
      `HTTP ${response.status}: ${response.statusText}`
    );
  }

  const result = await response.json();
  return (result as { data: TestMethodResult }).data;
}

/**
 * 執行 Streaming 測試 (SSE)
 */
export async function streamTest(
  params: StreamTestParams,
  handlers: {
    onEvent?: (event: StreamTestEvent) => void;
    onError?: (error: Error) => void;
    onComplete?: () => void;
  },
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(`${API_BASE}/agent/test-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
    credentials: 'include',
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { error?: string }).error ||
      `HTTP ${response.status}: ${response.statusText}`
    );
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        handlers.onComplete?.();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();

          if (data === '[DONE]') {
            handlers.onComplete?.();
            return;
          }

          try {
            const event = JSON.parse(data) as StreamTestEvent;
            handlers.onEvent?.(event);
          } catch (e) {
            console.warn('[SSE] Failed to parse event:', data, e);
          }
        }
      }
    }
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      return;
    }
    handlers.onError?.(error as Error);
    throw error;
  }
}

/**
 * Agent API
 */
export const agentApi = {
  streamTest: streamAgentTest,
  testMethod,
  streamSimple: streamTest,
};
